/**
 * 同一使用者多分頁會在 presence set 產生多筆相同 clientId、不同 connectionId 的成員。
 * 依 clientId 去重，同一個 clientId 只保留 timestamp 最大的一筆（最新的那條連線）。
 * 沒有 clientId 的成員原樣保留，不互相合併。
 *
 * @param {Array<Object>|null|undefined} members
 * @returns {Array<Object>}
 */
export function dedupeByClientId(members) {
    if (!Array.isArray(members)) {
        return [];
    }

    const byClientId = new Map();
    const withoutClientId = [];

    for (const member of members) {
        const clientId = member?.clientId;
        if (!clientId) {
            withoutClientId.push(member);
            continue;
        }

        const existing = byClientId.get(clientId);
        if (!existing || (member.timestamp ?? 0) > (existing.timestamp ?? 0)) {
            byClientId.set(clientId, member);
        }
    }

    return [...byClientId.values(), ...withoutClientId];
}

/**
 * presence set 內不重複 clientId 的總數。在線人數與排隊人數共用這個口徑。
 *
 * @param {Array<Object>|null|undefined} members
 * @returns {number}
 */
export function countUniqueClients(members) {
    return dedupeByClientId(members).length;
}

/**
 * 從已去重的成員陣列中挑出「自己」與「其他人」。
 * myClientId 為空字串或 undefined（未登入）時 me 一律為 null。
 * others 依 timestamp 降序排列，最新進入的排前面。
 *
 * @param {Array<Object>|null|undefined} members 已去重的成員陣列
 * @param {string} myClientId
 * @returns {{ me: Object|null, others: Array<Object> }}
 */
export function splitMeAndOthers(members, myClientId) {
    if (!Array.isArray(members)) {
        return { me: null, others: [] };
    }

    let me = null;
    const others = [];

    for (const member of members) {
        if (myClientId && member?.clientId === myClientId) {
            me = member;
        } else {
            others.push(member);
        }
    }

    others.sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0));

    return { me, others };
}
